import {
  CalendarIcon,
  CameraIcon,
  CheckCircleIcon,
  ClockIcon,
  CopyIcon,
  HourglassIcon,
  Typography,
} from "@cpj-ai/panel-npm-package";
import { FlexCalendarCalendarEventStatusChoices, JobPositionInterviewType } from "@graphql/generated";
import { Button, Card, CardBody, CardHeader, Chip, Divider } from "@heroui/react";
import dayjs from "dayjs";
import React, { useCallback, useState } from "react";
import toast from "react-hot-toast";

interface UpcomingInterviewCardProps {
  interviews?: JobPositionInterviewType[] | null;
}

interface InfoItemProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  iconColor: string;
}

const InfoItem: React.FC<InfoItemProps> = ({ icon, label, value, iconColor }) => (
  <div className="flex items-center gap-2">
    <div className={`bg-${iconColor} bg-opacity-[0.08] rounded-full p-1.5`}>{icon}</div>
    <div>
      <Typography className="text-xs text-default-500">{label}</Typography>
      <Typography className="text-sm font-medium">{value}</Typography>
    </div>
  </div>
);

const getRemainingDaysText = (days: number): string => {
  if (days > 1) return `${days} days left`;
  if (days === 1) return "Tomorrow";
  if (days === 0) return "Today";
  return "Passed";
};

const UpcomingInterviewCard: React.FC<UpcomingInterviewCardProps> = ({ interviews }) => {
  const [copied, setCopied] = useState(false);

  const handleCopyLink = useCallback((meetUrl: string | null | undefined) => {
    if (!meetUrl) return;

    navigator.clipboard.writeText(meetUrl).then(() => {
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 3000);
    });
  }, []);

  const activeInterview = interviews?.find(
    (interview) =>
      interview.event.status === FlexCalendarCalendarEventStatusChoices.SCHEDULED ||
      interview.event.status === FlexCalendarCalendarEventStatusChoices.STARTED,
  );

  if (!activeInterview) return null;

  const { datetime, meetUrl } = activeInterview.event;
  const startTime = dayjs(datetime[0]);
  const endTime = dayjs(datetime[1]);

  const interviewDuration = endTime.diff(startTime, "minute");
  const remainingDays = startTime.diff(dayjs(), "day");
  const formattedDate = startTime.format("dddd, MMM D");
  const formattedTimeSlot = `${startTime.format("HH:mm a")} - ${endTime.format("HH:mm a")}`;

  return (
    <div className="bg-primary-400 rounded-xl pt-[3px]">
      <Card className="border border-zinc-200 overflow-visible">
        <CardHeader className="flex justify-between items-center gap-3 px-5 pt-5 pb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
              <CameraIcon className="w-5 h-6 stroke-primary-400 ml-1 mt-1" />
            </div>
            <Typography className="text-sm text-default-500">Upcoming Interview</Typography>
          </div>
          <Chip color="danger" variant="flat" size="sm">
            {getRemainingDaysText(remainingDays)}
          </Chip>
        </CardHeader>

        <Divider />

        <CardBody className="px-5 py-4">
          <div className="grid grid-cols-2 gap-3 mb-4">
            <InfoItem
              icon={<CalendarIcon className="w-4 h-4 stroke-cyan-700" />}
              label="Date"
              value={formattedDate}
              iconColor="cyan-700"
            />
            <InfoItem
              icon={<ClockIcon className="w-4 h-4 fill-pink-600" />}
              label="Time"
              value={formattedTimeSlot}
              iconColor="pink-600"
            />
            <InfoItem
              icon={<CameraIcon className="w-4 h-4 stroke-green-700" />}
              label="Type"
              value="Online"
              iconColor="green-700"
            />
            <InfoItem
              icon={<HourglassIcon className="w-4 h-4 stroke-yellow-600" />}
              label="Duration"
              value={`${interviewDuration} min`}
              iconColor="yellow-600"
            />
          </div>

          <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 mb-1">
            <div className="flex items-center justify-between">
              <div className="flex-1 overflow-hidden">
                <Typography className="text-xs text-default-500 mb-1">Interview Link</Typography>
                <Typography className="text-sm font-mono truncate">{meetUrl}</Typography>
              </div>
              <Button
                isIconOnly
                size="sm"
                radius="lg"
                variant="bordered"
                color={copied ? "success" : "primary"}
                className="ml-2 min-w-0"
                onPress={() => handleCopyLink(meetUrl)}
                aria-label="Copy link"
              >
                {copied ? (
                  <CheckCircleIcon className="w-4 h-4 stroke-success-500" />
                ) : (
                  <CopyIcon className="w-4 h-4 fill-primary-500" />
                )}
              </Button>
            </div>
          </div>
          <Typography className="text-xs text-zinc-400">
            Link will be active 10 minutes before the interview. Please join the interview on time
          </Typography>
        </CardBody>
      </Card>
    </div>
  );
};

export default UpcomingInterviewCard;
